
import React, { useEffect, useState } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole, BugReport } from '../types';
import { Navigate } from 'react-router-dom';
import { Terminal, CheckCircle, RefreshCw, Trash2, ArrowUp, ArrowDown, Clock, AlertTriangle } from 'lucide-react';

export const WebAdminBugs: React.FC = () => {
    const { user } = useAuth();
    const [bugs, setBugs] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);

    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const loadBugs = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<BugReport[]>(`bugs.php?userId=${user.id}`);
            setBugs(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadBugs(); }, []);

    const handleUpdate = async (id: number, field: string, value: string) => {
        // Optimistic Update
        setBugs(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
        
        try {
            await apiRequest('bugs.php', 'PUT', { userId: user.id, id, [field]: value });
        } catch (e: any) {
            alert(e.message);
            loadBugs(); // Revert
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Eintrag löschen?")) return;
        try {
            await apiRequest(`bugs.php?id=${id}&userId=${user.id}`, 'DELETE');
            setBugs(prev => prev.filter(b => b.id !== id));
        } catch (e: any) { alert(e.message); }
    };

    const getPriorityColor = (p: string) => {
        switch(p) {
            case 'CRITICAL': return 'text-red-500 border-red-500 bg-red-900/20';
            case 'HIGH': return 'text-orange-500 border-orange-500 bg-orange-900/20';
            case 'MEDIUM': return 'text-yellow-500 border-yellow-500 bg-yellow-900/20';
            default: return 'text-green-500 border-green-500 bg-green-900/20';
        }
    };

    const activeBugs = bugs.filter(b => b.status !== 'DONE');
    const doneBugs = bugs.filter(b => b.status === 'DONE');

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                    <Terminal size={32}/> Entwicklungsaufgaben
                    </h1>
                    <p className="text-gray-400 text-sm">Bug Reports & Feature Requests (Internal)</p>
                </div>
                <Button variant="secondary" icon={RefreshCw} onClick={loadBugs}>Refresh</Button>
            </div>

            {/* ACTIVE LIST */}
            <div className="space-y-4">
                <h3 className="text-white font-bold uppercase flex items-center gap-2 border-b border-gray-700 pb-2">
                    <AlertTriangle className="text-yellow-500" size={18}/> Open Issues ({activeBugs.length})
                </h3>
                
                {activeBugs.length === 0 && <div className="text-gray-500 italic p-4 text-center border border-dashed border-gray-800 rounded">Alles erledigt! Gute Arbeit.</div>}

                {activeBugs.map(bug => (
                    <div key={bug.id} className="bg-[#1e1e24] border border-gray-700 rounded-lg p-4 flex flex-col md:flex-row gap-4 justify-between items-start transition-all hover:border-gray-500 shadow-lg">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${getPriorityColor(bug.priority)}`}>
                                    {bug.priority}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Clock size={12}/> {new Date(bug.created_at).toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-400 font-bold">
                                    von {bug.reporter_name}
                                </span>
                            </div>
                            <div className="text-white font-mono text-sm whitespace-pre-wrap bg-black/20 p-3 rounded border border-gray-800">
                                {bug.description}
                            </div>
                        </div>

                        <div className="flex flex-row md:flex-col gap-2 shrink-0">
                            <div className="flex bg-gray-900 rounded border border-gray-700 p-1 gap-1">
                                <button onClick={() => handleUpdate(bug.id, 'priority', 'LOW')} className={`p-1.5 rounded hover:bg-gray-700 ${bug.priority==='LOW'?'bg-green-900 text-green-400':''}`} title="Low"><ArrowDown size={14}/></button>
                                <button onClick={() => handleUpdate(bug.id, 'priority', 'MEDIUM')} className={`p-1.5 rounded hover:bg-gray-700 ${bug.priority==='MEDIUM'?'bg-yellow-900 text-yellow-400':''}`} title="Medium">-</button>
                                <button onClick={() => handleUpdate(bug.id, 'priority', 'HIGH')} className={`p-1.5 rounded hover:bg-gray-700 ${bug.priority==='HIGH'?'bg-orange-900 text-orange-400':''}`} title="High"><ArrowUp size={14}/></button>
                                <button onClick={() => handleUpdate(bug.id, 'priority', 'CRITICAL')} className={`p-1.5 rounded hover:bg-gray-700 ${bug.priority==='CRITICAL'?'bg-red-900 text-red-400':''}`} title="Critical">!</button>
                            </div>
                            
                            <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-500 w-full justify-center"
                                onClick={() => handleUpdate(bug.id, 'status', 'DONE')}
                            >
                                <CheckCircle size={16} className="mr-2"/> Done
                            </Button>
                            
                            <Button 
                                size="sm" 
                                variant="danger" 
                                className="w-full justify-center"
                                onClick={() => handleDelete(bug.id)}
                            >
                                <Trash2 size={16}/>
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {/* DONE LIST */}
            {doneBugs.length > 0 && (
                <div className="space-y-2 mt-8 pt-8 border-t border-gray-800 opacity-70 hover:opacity-100 transition-opacity">
                    <h3 className="text-green-500 font-bold uppercase flex items-center gap-2 pb-2">
                        <CheckCircle size={18}/> Completed ({doneBugs.length})
                    </h3>
                    
                    {doneBugs.map(bug => (
                        <div key={bug.id} className="bg-gray-900 border border-gray-800 rounded p-3 flex justify-between items-center text-sm">
                            <div className="text-gray-500 line-through truncate flex-1 pr-4">{bug.description}</div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => handleUpdate(bug.id, 'status', 'OPEN')} className="text-gray-500 hover:text-white text-xs underline">Re-Open</button>
                                <button onClick={() => handleDelete(bug.id)} className="text-red-900 hover:text-red-500"><Trash2 size={14}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
